
import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Import the blog components
import BlogCategories from '@/components/blog/BlogCategories';
import BlogCard from '@/components/blog/BlogCard';
import FeaturedPost from '@/components/blog/FeaturedPost';
import CategoryCard from '@/components/blog/CategoryCard';
import PopularPost from '@/components/blog/PopularPost';
import BlogSubscribe from '@/components/blog/BlogSubscribe';
import PopularTopics from '@/components/blog/PopularTopics';

// Import the blog data
import { 
  blogSchema, 
  categories, 
  industrySubcategories, 
  blogPosts,
  popularTopics
} from '@/components/blog/BlogData';

const Blog: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [activeTab, setActiveTab] = useState("all-posts");

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    if (categoryId !== "industry-specific") {
      setSelectedSubcategory(null);
    }
  };

  const handleSubcategorySelect = (subcategoryId) => {
    setSelectedSubcategory(subcategoryId);
  };

  // Filter posts based on selected category and subcategory
  const filteredPosts = blogPosts.filter(post => {
    if (selectedCategory === "all") return true;
    
    if (selectedCategory === "industry-specific" && selectedSubcategory) {
      return post.category === selectedCategory && post.industrySubcategory === selectedSubcategory;
    }
    
    return post.category === selectedCategory;
  });

  const getCategoryDescription = () => {
    if (selectedCategory === "all") {
      return "Expert guides, best practices, and industry trends to help your business leverage technology for growth and security.";
    }
    
    if (selectedCategory === "industry-specific" && selectedSubcategory) {
      const subcategory = industrySubcategories.find(sub => sub.id === selectedSubcategory);
      return `Specialized IT solutions and insights for ${subcategory.name}.`;
    }
    
    const category = categories.find(cat => cat.id === selectedCategory);
    return category ? category.description : "";
  };

  // Helper function to get category name from category ID
  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : categoryId;
  };

  // Helper function to get subcategory name from subcategory ID
  const getSubcategoryName = (subcategoryId) => {
    const subcategory = industrySubcategories.find(sub => sub.id === subcategoryId);
    return subcategory ? subcategory.name : "";
  };
  
  return (
    <div className="min-h-screen">
      <SEO 
        title="IT Management Blog | Cybersecurity & Technology Insights"
        description="Expert insights, guides, and best practices on IT management, cybersecurity, cloud solutions, and technology trends for small businesses."
        canonicalUrl="https://itmgmtsol.com/blog"
        schema={blogSchema}
      />
      <Navbar />
      
      <section className="bg-itmgmt-dark-blue text-white py-16">
        <div className="container-wide">
          <h1 className="heading-xl mb-4">IT Management & Technology Insights</h1>
          <p className="text-xl max-w-3xl">
            {getCategoryDescription()}
          </p>
        </div>
      </section>
      
      <section className="py-16 bg-gray-50">
        <div className="container-wide">
          <Tabs defaultValue="all-posts" value={activeTab} onValueChange={setActiveTab} className="mb-8">
            <TabsList className="mb-6">
              <TabsTrigger value="all-posts" onClick={() => handleCategorySelect("all")}>
                All Posts
              </TabsTrigger>
              <TabsTrigger value="categories">Categories</TabsTrigger>
              <TabsTrigger value="featured">Featured Articles</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all-posts" className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="md:col-span-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {filteredPosts.map(post => (
                      <BlogCard 
                        key={post.id} 
                        post={post} 
                        categoryName={getCategoryName(post.category)}
                        subcategoryName={post.industrySubcategory ? getSubcategoryName(post.industrySubcategory) : undefined}
                      />
                    ))}
                  </div>
                </div>
                
                <div className="md:col-span-1">
                  <BlogCategories 
                    selectedCategory={selectedCategory} 
                    onSelectCategory={handleCategorySelect}
                    selectedSubcategory={selectedSubcategory}
                    onSelectSubcategory={handleSubcategorySelect}
                    categories={categories}
                    industrySubcategories={industrySubcategories}
                  />
                  
                  <div className="mt-8 bg-white p-6 rounded-lg shadow-sm">
                    <h3 className="text-lg font-bold mb-4">Popular Articles</h3>
                    <div className="space-y-4">
                      {blogPosts.slice(0, 3).map(post => (
                        <PopularPost key={`popular-${post.id}`} post={post} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="categories">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {categories.slice(1).map(category => (
                  <CategoryCard 
                    key={category.id} 
                    category={category} 
                    subcategories={category.id === "industry-specific" ? industrySubcategories : undefined}
                    onSelectCategory={handleCategorySelect}
                    onSelectSubcategory={handleSubcategorySelect}
                    setActiveTab={setActiveTab}
                  />
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="featured">
              <div className="grid grid-cols-1 gap-8">
                <FeaturedPost 
                  post={blogPosts[0]} 
                  categoryName={getCategoryName(blogPosts[0].category)} 
                />
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {blogPosts.slice(1, 4).map(post => (
                    <BlogCard 
                      key={`featured-${post.id}`} 
                      post={post} 
                      categoryName={getCategoryName(post.category)}
                    />
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          <PopularTopics topics={popularTopics} />
        </div>
      </section>
      
      <BlogSubscribe />
      
      <Footer />
    </div>
  );
};

export default Blog;
